<template>
  <v-card tile height="100%">
    <v-toolbar color="primaryLight" dark dense>
      <v-toolbar-title>Gestión de Usuarios</v-toolbar-title>
      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            fab
            small
            style="top: 40px"
            right
            absolute
            color="secondary"
            @click="showAddUsuarioDialog"
          >
            <v-icon color="black">mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Añadir usuario</span>
      </v-tooltip>
    </v-toolbar>

    <v-card-text class="flex-grow-1 overflow-y-auto d-flex flex-column">
      <v-container class="py-0 ma-0 h-100" fluid>
        <v-row dense class="mt-0 no-spacing-row">
          <v-col cols="3">
            <v-text-field
              label="Nombre"
              v-model="nombreSelected"
              clearable
              outlined
              dense
              @blur="filterUsuarios"
            />
          </v-col>
        </v-row>

        <v-row class="no-spacing-row">
          <v-col>
            <span class="subtitle-1 black--text">Nº de usuarios: {{ usuariosFilter.length }}</span>
          </v-col>
        </v-row>
      </v-container>

      <v-container class="py-0 ma-0 flex-grow-1 overflow-y-auto" fluid>
        <div class="d-flex flex-column" style="max-height: 100%;">
          <v-data-table
            style="overflow: auto"
            :headers="computedHeaders"
            :items="usuariosFilter"
            item-key="ID_USUARIO"
            fixed-header
            hide-default-footer
            disable-pagination
          >
            <template #[`item.ROL`]="{ item }">
              <span style="color: black; align-items: baseline;">{{ item.ROL || '—' }}</span>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn icon small color="blue" @click="editarUsuario(item)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small color="red" @click="eliminarUsuario(item)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>
      </v-container>
    </v-card-text>

    <dialog-edit-usuario
      :show="dialogEditar"
      :is-edit="!!(usuarioEdit && usuarioEdit.ID_USUARIO)"
      :edited-usuario="usuarioEdit"
      :roles="roles"
      @addUsuario="handleAddUsuario"
      @editUsuario="handleEditUsuario"
      @cancelar="dialogEditar = false"
    />
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { normalizeText, clearFilters } from '@/helpers/Utils.js'
import DialogEditUsuario from '../usuarios/DialogAddEditUsuarios.vue'
import { ROLES } from '@/helpers/constants'

export default {
  components: {
    DialogEditUsuario
  },
  props: {
  },

  data: () => ({
    nombreSelected: null,
    ocultosSelected: null,
    tableFilters: [],
    usuariosFilter: [],
    searchUser: '',
    showPasswords: [],
    dialogEditar: false,
    usuarioEdit: null,

    headers: [
      { text: 'Nombre', value: 'USUARIO', align: 'left', sortable: true, initialShow: true },
      { text: 'Rol', value: 'ROL.ROL', align: 'center', sortable: false, initialShow: true },
      { text: 'Acciones', value: 'actions', align: 'right', sortable: false, initialShow: true }
    ]

  }),

  computed: {
    ...mapGetters('usuario', ['roles', 'usuariosAll']),
    ...mapGetters('auth', ['usuario']),
    usuarios () {
      return this.usuariosAll
    },

    computedHeaders () {
      if (!this.tableFilters.length) {
        return this.headers
      }

      // Si los filtros están definidos como objetos
      return this.headers.filter(h => this.tableFilters.includes(h))
    },

    filteredUsuariosPanel () {
      const search = normalizeText(this.searchUser || '')
      return this.usuariosFilter.filter(u =>
        normalizeText(u.NOMBRE).includes(search)
      )
    },
    isAdmin () {
      return this.usuario?.ROL === ROLES.ADMIN
    }
  },

  watch: {
    usuarios () {
      this.usuariosFilter = [...this.usuarios]
      this.filterUsuarios()
    },
    nombreSelected () {
      this.filterUsuarios()
    },
    ocultosSelected () {
      this.filterUsuarios()
    }
  },

  methods: {
    ...mapActions('usuario', ['fetchUsuarios', 'createUsuario', 'deleteUsuario', 'updateUsuario']),
    filterUsuarios () {
      let filtered = [...this.usuarios]

      if (this.nombreSelected) {
        const search = normalizeText(this.nombreSelected)
        filtered = filtered.filter(u => normalizeText(u.USUARIO).includes(search))
      }
      this.usuariosFilter = filtered
    },

    resetFilters () {
      this.tableFilters = this.headers.filter(x => x.initialShow)
      const filters = {
        nombreSelected: null,
        ocultosSelected: null
      }

      clearFilters(filters)
      Object.keys(filters).forEach(key => {
        this[key] = filters[key]
      })
      this.usuariosFilter = [...this.usuarios]
    },

    togglePassword (id) {
      this.$set(this.showPasswords, id, !this.showPasswords[id])
    },
    getMaskedPassword (usuario) {
      const pass = usuario.PASSWD || ''
      return '*'.repeat(pass.length || 8)
    },

    editarUsuario (usuario) {
      this.usuarioEdit = { ...usuario }
      this.dialogEditar = true
    },

    async handleAddUsuario (usuario) {
      try {
        await this.createUsuario(usuario)
        this.dialogEditar = false
        this.usuarioEdit = null
        this.$notify({ text: 'Usuario añadido correctamente', color: 'green' })
      } catch (err) {
        console.error('Error al añadir usuario:', err)
        this.$notify({ text: 'Error al añadir usuario', color: 'red' })
      }
    },

    async handleEditUsuario (usuario) {
      try {
        const usuarioCopia = { ...usuario }

        if (!usuarioCopia.ID_USUARIO) {
          throw new Error('Falta ID_USUARIO para actualizar')
        }

        await this.updateUsuario(usuarioCopia)
        this.dialogEditar = false
        this.usuarioEdit = null
        this.$notify({ text: 'Usuario actualizado correctamente', color: 'green' })
      } catch (err) {
        console.error('Error al actualizar usuario:', err)
        this.$notify({ text: 'Error al actualizar usuario', color: 'red' })
      }
    },

    async eliminarUsuario (usuario) {
      const confirmado = confirm(`¿Seguro que deseas eliminar el usuario "${usuario.USUARIO}"?`)
      if (!confirmado) return

      try {
        await this.deleteUsuario(usuario.ID_USUARIO)
      } catch (err) {
        console.error('Error eliminando usuario:', err)
        this.$notify({ text: 'Error al eliminar el usuario', color: 'red' })
      }
    },

    showAddUsuarioDialog () {
      this.usuarioEdit = null
      this.dialogEditar = true
    },
    async guardarUsuario (usuario) {
      try {
        if (usuario.ID_USUARIO) {
          await this.updateUsuario(usuario)
        } else {
          await this.createUsuario(usuario)
        }

        this.dialogEditar = false
        this.usuarioEdit = null
      } catch (error) {
        console.error('Error guardando usuario:', error)
        this.$notify({ text: 'Error al guardar el usuario.', color: 'red' })
      }
    }

  },
  mounted () {
    this.usuariosFilter = [...this.usuarios]
  }

}
</script>

<style scoped>
.table {
  max-height: 500px;
  overflow: auto;
}
.fill-height {
  height: 100vh;
}

.v-card {
  width: 100%;
}

.v-sheet.v-card {
  border-radius: 0;
}

.no-spacing-row {
  height: fit-content;
  padding-bottom: 5px;
  margin-top: 0px;
}

.no-spacing-row ::v-deep(.v-text-field__details) {
  display: none;
}

.no-spacing-row ::v-deep(.col) {
  height: fit-content;
}
</style>
